// @flow
import React, {useState, useEffect, useRef, useCallback} from 'react';
import {useHistory} from 'react-router-dom';
import {
  Divider,
  Grid,
  makeStyles,
  CircularProgress,
  Typography,
} from '@material-ui/core';
import {colors} from '../../../../../../assets/styles/colors';
import CustomButton from '../../../../../../elements/CustomButton/CustomButton';
import {useQrCode} from '../../../../../../utils/qr/hooks';

import {
  getDeviceStatusService,
  getDevicesService,
  connectWhatsappWebDeviceService,
} from '../../../promises';
import CustomModal from '../../../../../../elements/CustomModal/CustomModal';
import {validatePhoneNumber} from '../../../../../../utils/phone';
import Toast from '../../../../../../elements/Toast';

import type {StoreDevice} from '../../../../../../entities';
import PhoneInput from '../../../../../../elements/PhoneInput';
import InlineLoader from '../../../../../../elements/LoadingSpinner/InlineLoader';
import {useDeviceReconnect, useStoreDevices} from '../../../utils';
import {Link} from 'react-router-dom/cjs/react-router-dom';
import {TIENDADA_LINKS} from '../../../../../../constants';
import {useConversationsSynchronizing} from '../../../utils/conversation';

const useStyles = makeStyles(theme => ({
  main: {
    padding: theme.spacing(3, 4),
  },
  title: {
    fontSize: 18,
    color: colors.grey900,
    fontWeight: 600,
    margin: 0,
    textAlign: 'center',

    [theme.breakpoints.up('sm')]: {
      textAlign: 'left',
    },
  },
  textInfo: {
    fontSize: 16,
    fontWeight: 400,
    color: colors.grey700,
  },
  textInfoError: {
    fontSize: 16,
    fontWeight: 400,
    color: colors.error,
  },
  link: {
    color: colors.primary,
    textDecoration: 'none',
    fontWeight: 500,
    fontSize: 16,
    padding: 0,
  },
  modalTitle: {
    fontSize: 18,
    color: colors.grey900,
    fontWeight: 600,
    margin: 0,
    textAlign: 'center',
  },
  header: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    [theme.breakpoints.up('sm')]: {
      flexDirection: 'row',
      alignItems: 'center',
    },
  },
  divider: {
    margin: theme.spacing(2.5, 0),
  },
  qrContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 200,
    minWidth: 200,
    width: '100%',
    aspectRatio: '1/1',
    borderRadius: '.5rem',
    padding: '.75rem',
    background: colors.grey100,
    marginLeft: 'auto',
    position: 'relative',

    '& img': {
      width: '100%',
      height: '100%',
      objectFit: 'cover',
    },
  },
  loader: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
    borderRadius: '.5rem',
    padding: '.75rem',
  },
  contentGrid: {
    display: 'flex',
    flexDirection: 'column-reverse',
    gap: '1rem',
    alignItems: 'center',

    [theme.breakpoints.up('md')]: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      flexWrap: 'nowrap',
    },
  },
  overlay: {
    position: 'absolute',
    background: 'rgba(255,255,255,.5)',
    width: '100%',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: '.5rem',
    top: 0,
    left: 0,
    backdropFilter: 'blur(5px)',
    zIndex: 1,
  },
  modalRoot: {
    maxWidth: 400,
    paddingBlock: '2rem',
    margin: '0 auto',
    textAlign: 'center',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '1rem',
  },
  modalContainer: {
    width: '100vw',
  },
}));

const MAX_CALLS = 20;

export default function WsspConfig() {
  const classes = useStyles();
  const history = useHistory();

  const {updateConversationSynchronizing} = useConversationsSynchronizing();

  const callCount = useRef(0);

  const phoneRef = useRef('');
  const [isConnecting, setIsConnecting] = useState(false);
  const [deviceStatusText, setDeviceStatusText] = useState(
    'Generando código QR'
  );

  const [connectError, setConnectError] = useState<string | null>(null);

  const [devices, setDevices] = useState<StoreDevice[]>([]);

  const [isFetchingQr, setIsFetchingQr] = useState(false);
  const [phoneReconnect, setPhoneReconnect] = useState(null);
  const [qrCodeReconnect, setQrCodeReconnect] = useState(null);
  const [qrCode, setQrCode] = useState();
  const [modalPhoneNumberOpen, setModalPhoneNumberOpen] = useState(true);
  const {
    phoneReconnect: phoneStoreReconnect,
    qrCodeReconnect: qrCodeStoreReconnect,
    resetReconnect,
  } = useDeviceReconnect();
  const {
    storeDevices,
    reconnectDevice,
    deleteDeviceWithHistory,
  } = useStoreDevices({
    refetchOnMount: true,
  });

  const [phoneError, setPhoneError] = useState(false);

  const {qrDataUrl, isGenerating} = useQrCode(
    qrCodeReconnect ? qrCodeReconnect : qrCode
  );

  useEffect(() => {
    getDevicesService(['WHATSAPP_WEB']).then(res => {
      setDevices(res.data);
    });
  }, [qrDataUrl]);

  useEffect(() => {
    const phone = phoneRef.current;

    const currentDevice = [...devices]
      .reverse()
      .find(
        device =>
          phone.includes(device.identifier) ||
          phoneReconnect?.includes(device.identifier)
      );

    if (!currentDevice) return;

    const interval = setInterval(() => {
      callCount.current += 1;

      if (callCount.current >= MAX_CALLS) {
        setConnectError('Ocurrió un error al intentar conectar el dispositivo');
        clearInterval(interval);
      }
      getDeviceStatusService(currentDevice.id, 'WHATSAPP_WEB')
        .then(({status}) => {
          if (status === 'CONNECTING') {
            setIsConnecting(true);
            setDeviceStatusText('Conectando dispositivo...');
            return;
          }

          if (status === 'DISCONNECTED') {
            setIsConnecting(false);
            return;
          }

          if (status === 'CONNECTED') {
            updateConversationSynchronizing(true);
            resetReconnect();
            setDeviceStatusText('Dispositivo conectado');
            clearInterval(interval);
            const searchParams = new URLSearchParams(window.location.search);
            const path = `/chat/multi-chat?${searchParams.toString()}`;
            history.push(path);
            setIsConnecting(false);
          }
        })
        .catch(err => {
          console.log(err);
          setIsConnecting(false);
          if (err.error === 'CrmMessagingDeviceError') {
            setConnectError(
              'El número de teléfono ya se encuentra conectado a TiendaDa'
            );
            return;
          }
          setConnectError(
            'Ocurrió un error al intentar conectar el dispositivo'
          );
        });
    }, 3000);

    return () => clearInterval(interval);
  }, [
    devices,
    history,
    phoneReconnect,
    qrDataUrl,
    resetReconnect,
    updateConversationSynchronizing,
  ]);

  useEffect(() => {
    if (qrCodeStoreReconnect) {
      setQrCodeReconnect(qrCodeStoreReconnect);
    }
  }, [qrCodeStoreReconnect]);

  useEffect(() => {
    if (phoneStoreReconnect) {
      setPhoneReconnect(phoneStoreReconnect);
    }
  }, [phoneStoreReconnect]);

  useEffect(() => {
    if (phoneReconnect && qrCodeReconnect) {
      setModalPhoneNumberOpen(false);
    }
  }, [phoneReconnect, qrCodeReconnect]);

  useEffect(() => {
    return () => {
      resetReconnect();
    };
  }, [resetReconnect]);

  const handleChooseSocial = () => {
    history.push('/chat/configuration');
  };

  const handleSubmit = (event: Event) => {
    event.preventDefault();

    const phoneInput = phoneRef.current;
    const valid = validatePhoneNumber(`+${phoneInput}`);

    if (!valid) {
      setPhoneError(true);

      setTimeout(() => {
        setPhoneError(false);
      }, 3000);

      return;
    }

    const deviceInStoreDisconnected = storeDevices
      .filter(device => device.service === 'WHATSAPP_WEB')
      .find(
        device =>
          device.identifier.includes(phoneInput) &&
          device.state === 'DISCONNECTED'
      );

    if (deviceInStoreDisconnected) {
      reconnectDevice(deviceInStoreDisconnected.id, 'WHATSAPP_WEB');
      return;
    }

    getQrCode(phoneInput);
  };

  const getQrCode = (phone: string) => {
    const withOutCountryCode = phone.replace('+', '');

    setIsFetchingQr(true);
    connectWhatsappWebDeviceService(withOutCountryCode)
      .then(res => {
        setQrCode(res.qrCode);
        setModalPhoneNumberOpen(false);
      })
      .catch(err => {
        if (err.error === 'CrmMessagingDeviceError') {
          setConnectError(
            'El número de teléfono ya se encuentra conectado a TiendaDa'
          );
          return;
        }
        setConnectError('Ocurrió un error al intentar conectar el dispositivo');
      })
      .finally(() => {
        setIsFetchingQr(false);
      });
  };

  const removeDeviceInConnectingState = useCallback(() => {
    getDevicesService(['WHATSAPP_WEB']).then(res => {
      const devices = res.data;
      const deviceToRemove =
        devices &&
        devices.filter(
          d => d.state === 'CONNECTING' && d.accountSynchronized === false
        )[0];
      if (!deviceToRemove) return;
      deleteDeviceWithHistory(deviceToRemove.id, deviceToRemove.service);
    });
  }, [deleteDeviceWithHistory]);

  useEffect(() => {
    removeDeviceInConnectingState();

    return () => {
      removeDeviceInConnectingState();
    };
  }, [removeDeviceInConnectingState]);

  return (
    <div className={classes.main}>
      <div className={classes.header}>
        <p className={classes.title}>Conecta WhatsApp a TiendaDa</p>
        <CustomButton onClick={handleChooseSocial}>
          Elegir otra red social
        </CustomButton>
      </div>
      <Divider className={classes.divider} />
      <Grid container spacing={2} className={classes.contentGrid}>
        <Grid item xs={12} md={8}>
          {isConnecting && (
            <p className={classes.textInfo}>
              Se esta conectando tu cuenta de WhatsApp, por favor espera...
            </p>
          )}
          {connectError && !modalPhoneNumberOpen && (
            <p className={classes.textInfoError}>
              Ocurrio un error, ingresa directamente a la{' '}
              <Link className={classes.link} to="/chat/multi-chat">
                página Inbox
              </Link>{' '}
              o comunicate con{' '}
              <CustomButton
                href={TIENDADA_LINKS.customerSupport.general}
                target="_blank"
                rel="noopener noreferrer"
                variant="text"
                className={classes.link}
              >
                nosotros.
              </CustomButton>
            </p>
          )}
          <p className={classes.textInfo}>1. Abre WhatsApp desde tu celular.</p>
          <p className={classes.textInfo}>
            2. Haz click al menu o los settings de tu celular.
          </p>
          <p className={classes.textInfo}>
            3. Haz click a “Dispositivos Conectados”.
          </p>
          <p className={classes.textInfo}>
            4. Haz click a “Vincular un dispositivo”.
          </p>
          <p className={classes.textInfo}>
            5. Escanea el código QR que esta en esta pantalla.
          </p>
        </Grid>
        <Grid item xs={12} md={4} alignItems="center" justifyContent="center">
          <picture className={classes.qrContainer}>
            <InlineLoader
              size={42}
              loading={isConnecting}
              className={classes.loader}
            />
            {qrDataUrl && <img src={qrDataUrl} alt="" />}

            {(isGenerating || isFetchingQr) && (
              <div className={classes.loader}>
                <div className={classes.overlay}>
                  <CircularProgress />
                  <Typography>{deviceStatusText}</Typography>
                </div>
              </div>
            )}
          </picture>
        </Grid>
      </Grid>

      <CustomModal
        open={modalPhoneNumberOpen}
        onClose={() => {
          setModalPhoneNumberOpen(false);
          handleChooseSocial();
        }}
        classes={{
          container: classes.modalContainer,
        }}
      >
        <form className={classes.modalRoot} onSubmit={handleSubmit}>
          <Typography className={classes.modalTitle} align="center">
            Ingresa tu número de celular para conectarte a WhatsApp
          </Typography>

          <PhoneInput
            name="phone"
            label="Número de celular"
            onChange={phone => {
              phoneRef.current = phone;
            }}
            helperText={phoneError ? 'Número de celular inválido' : undefined}
          />
          <CustomButton
            fullWidth
            type="submit"
            variant="contained"
            className={classes.continueButton}
            disabled={isFetchingQr}
          >
            Continuar
          </CustomButton>
        </form>
      </CustomModal>
      <Toast
        security="error"
        color="error"
        open={!!connectError}
        onClose={() => setConnectError(null)}
        closeDelay={10000}
      >
        {connectError}
      </Toast>
    </div>
  );
}
